/**
 * The browser half of a passkey — `docs/identity-and-sign-in.md` §4.2.
 *
 * `navigator.credentials` is what shows the device prompt; everything either
 * side of it is ours. Two jobs live here because they are the same three
 * conversions in opposite directions, and splitting them across the setup
 * dialog and the sign-in page is how the two would drift:
 *
 *   - **create** — enrolling, from My profile or the setup dialog.
 *   - **get** — answering a challenge at sign-in or a step-up.
 *
 * **base64url, both ways.** The WebAuthn API speaks `ArrayBuffer` and JSON
 * does not, so every id, challenge and blob crosses as base64url — which is
 * what `@simplewebauthn/server` reads on the other side. Keeping the two
 * conversions in one file beside the two calls is what makes that checkable.
 */
import { ApiError } from '@rowted/shared'
import { request } from './api.js'
import type { AccountRequest } from '../account/account-api.js'

const decode = (value: string): Uint8Array => {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='))
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

const encode = (buffer: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

/** True when this browser can do passkeys at all. */
export const passkeysWork = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.PublicKeyCredential)

interface CreationOptionsJSON {
  challenge: string
  rp: { id: string; name: string }
  user: { id: string; name: string; displayName: string }
  pubKeyCredParams: { alg: number; type: 'public-key' }[]
  timeout?: number
  excludeCredentials?: { id: string; type: 'public-key'; transports?: string[] }[]
  authenticatorSelection?: Record<string, unknown>
  attestation?: string
}

interface RequestOptionsJSON {
  challenge: string
  rpId?: string
  timeout?: number
  userVerification?: string
  allowCredentials?: { id: string; type: 'public-key'; transports?: string[] }[]
}

/**
 * Enrol a passkey and hand the result to the server.
 *
 * The label is the person's own words for the device; the server stores it
 * unchanged, because a device renamed next year should keep the name it was
 * given rather than a guess recomputed at read time.
 *
 * `send` is the caller's request client, because the same two endpoints exist
 * on both sides at the same relative paths (`routes/platform-me.ts`) and this
 * file must not decide which app it is in. Everything else about it — the
 * three base64url conversions, the prompt — is identical either way, which is
 * exactly why it is one function and not two.
 */
export async function createPasskey<T>(
  send: AccountRequest, label: string, syncable = true,
): Promise<T> {
  const options = await send<CreationOptionsJSON>(
    '/me/methods/passkey/options', { method: 'POST', body: {} })

  const created = await navigator.credentials.create({
    publicKey: {
      ...options,
      /*
       * Board 3's *Also sync it through my password manager*. Unticked asks
       * for `platform`, which keeps the key on this device; ticked leaves the
       * attachment unset, which is what lets 1Password or iCloud Keychain hold
       * it and what makes it work on the person's other devices. The server
       * decides everything else about the options — this is the one field the
       * person in front of the dialog is answering.
       */
      authenticatorSelection: syncable
        ? options.authenticatorSelection
        : { ...options.authenticatorSelection, authenticatorAttachment: 'platform' },
      challenge: decode(options.challenge) as unknown as BufferSource,
      user: { ...options.user, id: decode(options.user.id) as unknown as BufferSource },
      excludeCredentials: options.excludeCredentials?.map((one) => ({
        ...one,
        id: decode(one.id) as unknown as BufferSource,
        transports: one.transports as AuthenticatorTransport[] | undefined,
      })),
    } as PublicKeyCredentialCreationOptions,
  }) as PublicKeyCredential | null

  if (!created) throw new ApiError('validation_failed', 'That passkey was not created.')

  const response = created.response as AuthenticatorAttestationResponse
  return send<T>('/me/methods/passkey/verify', {
    method: 'POST',
    body: {
      label,
      response: {
        id: created.id,
        rawId: encode(created.rawId),
        type: created.type,
        clientExtensionResults: created.getClientExtensionResults(),
        response: {
          clientDataJSON: encode(response.clientDataJSON),
          attestationObject: encode(response.attestationObject),
          transports: response.getTransports?.() ?? [],
        },
      },
    },
  })
}

/**
 * Answer a challenge with a passkey.
 *
 * It returns the assertion rather than posting it: the caller is the one that
 * knows which endpoint the answer belongs to — `/auth/mfa/verify` at sign-in,
 * a step-up route before a sensitive action — and the shape of the answer is
 * the same either way.
 */
export async function assertPasskey(challenge: string): Promise<unknown> {
  const options = await request<RequestOptionsJSON>('/auth/passkey/options', {
    method: 'POST', body: { challenge },
  })

  const got = await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: decode(options.challenge) as unknown as BufferSource,
      allowCredentials: options.allowCredentials?.map((one) => ({
        ...one,
        id: decode(one.id) as unknown as BufferSource,
        transports: one.transports as AuthenticatorTransport[] | undefined,
      })),
      userVerification: options.userVerification as UserVerificationRequirement | undefined,
    } as PublicKeyCredentialRequestOptions,
  }) as PublicKeyCredential | null

  if (!got) throw new ApiError('mfa_invalid', 'That passkey was not used.')

  const response = got.response as AuthenticatorAssertionResponse
  return {
    id: got.id,
    rawId: encode(got.rawId),
    type: got.type,
    clientExtensionResults: got.getClientExtensionResults(),
    response: {
      clientDataJSON: encode(response.clientDataJSON),
      authenticatorData: encode(response.authenticatorData),
      signature: encode(response.signature),
      userHandle: response.userHandle ? encode(response.userHandle) : null,
    },
  }
}
